'use client';

import { createContext, useContext } from 'react';

import { GUEST_FREE_MESSAGES } from '~/lib/guest-chat-config';

/**
 * Server-decided guest state for the chat page.
 *
 * The [characterSlug] layout (a Server Component) knows authoritatively
 * whether the visitor has a session and how many free guest messages remain
 * (httpOnly cookie) and provides both here — the page can't rely on
 * client-side auth state, which hydrates late and is indistinguishable from
 * "actually logged out" during the first render.
 */
interface GuestMode {
  isGuest: boolean;
  /** Free messages left when the page was served (guests only). */
  initialRemaining: number;
}

const GuestModeContext = createContext<GuestMode>({
  isGuest: false,
  initialRemaining: GUEST_FREE_MESSAGES,
});

export function GuestModeProvider({
  isGuest,
  initialRemaining,
  children,
}: GuestMode & { children: React.ReactNode }) {
  return (
    <GuestModeContext.Provider value={{ isGuest, initialRemaining }}>
      {children}
    </GuestModeContext.Provider>
  );
}

export function useGuestMode() {
  return useContext(GuestModeContext);
}
